<template>
  <div id="serviceplanner-landingpage">
    <navigation fancy :utm="utm" />

    <section class="hero" :style="bannerStyle">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-multiline is-mobile is-vcentered py-6 my-6">
            <div
              class="column column-voodoo is-12-mobile is-10-tablet is-offset-1-tablet is-5-desktop is-offset-0-desktop is-5-fullhd"
            >
              <div class="card is-shadowless has-background-light">
                <div class="card-content has-text-primary">
                  <p>{{ $t("views.serviceplanner.banner-new") }}</p>
                  <h1 class="title is-marginless is-size-3 is-size-4-mobile">
                    {{ $t("views.serviceplanner.banner") }}
                  </h1>
                </div>
              </div>
              <a
                :href="registerLink"
                class="hero-cta is-block py-3 px-5 has-background-primary has-text-white title is-4"
              >
                {{ $t("views.serviceplanner.banner-cta") }}
              </a>
            </div>
            <div
              class="column is-12-mobile is-10-tablet is-7-desktop is-offset-1-tablet is-offset-0-desktop"
            >
              <img
                alt=""
                class="banner-mockup"
                :src="localizedImage('banner-laptop')"
              />
            </div>
          </div>
        </div>
      </div>
      <a
        href="#serviceplanner-overview"
        class="hero-scroller has-text-white has-text-centered"
      >
        <fa icon="angle-double-down" class="fa-2x" />
      </a>
    </section>

    <section class="section is-small" id="serviceplanner-overview">
      <div class="container">
        <h2 class="title is-size-2 is-size-4-mobile">
          {{ $t("views.serviceplanner.overview.headline") }}
        </h2>
        <p class="mb-6">
          {{ $t("views.serviceplanner.overview.description") }}
        </p>

        <h3 class="title is-size-3 is-size-5-mobile">
          {{ $t("views.serviceplanner.benefits.headline") }}
        </h3>
        <ul class="has-repguide-bullets">
          <li
            class="mt-4"
            v-html="$t('views.serviceplanner.benefits.bullet-1')"
          />
          <li
            class="mt-4"
            v-html="$t('views.serviceplanner.benefits.bullet-2')"
          />
          <li
            class="mt-4"
            v-html="$t('views.serviceplanner.benefits.bullet-3')"
          />
          <li
            class="mt-4"
            v-html="$t('views.serviceplanner.benefits.bullet-4')"
          />
          <li
            class="mt-4"
            v-html="$t('views.serviceplanner.benefits.bullet-5')"
          />
        </ul>
        <div class="has-text-centered pt-6">
          <a
            :href="registerLink"
            class="button is-primary is-medium"
            style="white-space: normal; height: unset"
          >
            {{ $t("views.serviceplanner.benefits.cta") }}
          </a>
        </div>
      </div>
    </section>

    <section class="section is-small has-background-light">
      <div class="container">
        <h2 class="title is-size-2 is-size-4-mobile">
          {{ $t("views.serviceplanner.preview.headline") }}
        </h2>
        <p class="mb-6">
          {{ $t("views.serviceplanner.preview.description") }}
        </p>
        <div
          v-for="(step, index) in previewSteps"
          :key="step.headline"
          :class="[
            'columns is-vcentered my-6 is-justify-content-flex-end',
            { 'is-flex-direction-row-reverse': index % 2 === 0 },
          ]"
        >
          <div
            :class="[
              'column is-half-widescreen',
              { 'is-offset-1-tablet': index % 2 === 0 },
            ]"
          >
            <div class="is-flex is-align-items-center mb-3">
              <span
                class="icon is-large is-flex-shrink-0 mr-3 has-text-primary"
                style="background: rgba(233, 74, 16, 0.12); border-radius: 6px"
              >
                <fa :icon="step.icon" class="fa-lg" />
              </span>
              <div>
                <p
                  class="is-size-7 has-text-weight-semibold"
                  style="line-height: 1.2"
                >
                  <span class="has-text-primary is-size-6">
                    {{
                      $t("views.serviceplanner.preview.step", {
                        step: index + 1,
                      })
                    }}
                  </span>
                </p>
                <h3 class="title is-4 my-0 has-text-dark">
                  {{ step.headline }}
                </h3>
              </div>
            </div>
            <p>{{ step.description }}</p>
            <ul class="has-repguide-bullets mt-4">
              <li
                v-for="bullet in step.bullets"
                :key="bullet"
                class="mt-2"
                v-html="bullet"
              />
            </ul>
          </div>

          <div
            :class="[
              'column is-two-fifths-tablet',
              { 'is-offset-1-tablet': index % 2 !== 0 },
            ]"
          >
            <figure class="image" :class="{ 'has-overlay': step.overlay }">
              <img alt="" :src="step.image" />
              <img
                v-if="step.overlay"
                alt=""
                class="preview-overlay"
                :src="step.overlay"
              />
            </figure>
          </div>
        </div>

        <div class="has-text-centered pt-6">
          <h2 class="title is-size-2 is-size-4-mobile has-text-secondary">
            {{ $t("views.serviceplanner.outro.headline") }}
          </h2>

          <a
            :href="registerLink"
            class="button is-primary is-medium"
            style="white-space: normal; height: unset"
          >
            {{ $t("views.serviceplanner.benefits.cta") }}
          </a>

          <img
            alt=""
            :src="localizedImage('banner-laptop')"
            style="
              display: block;
              max-width: 720px;
              margin: 0 auto;
              width: 100%;
            "
            class="pt-4"
          />
        </div>
      </div>
    </section>

    <footerNavigation />
  </div>
</template>

<script>
import bannerImage from "../../assets/serviceplanner-banner.webp";
import footerNavigation from "../../components/footerNavigation.vue";
import navigation from "../../components/navigation.vue";
import { getRegisterUrl } from "../../register-url.js";

export default {
  name: "serviceplanner-landingpage",
  props: {
    utm: { type: Object, default: () => {} },
  },
  components: {
    navigation,
    footerNavigation,
  },
  computed: {
    registerLink() {
      const UTM = { ...this.utm, utm_content: "landingpage_serviceplanner" };
      return getRegisterUrl(this.$route.params.locale, UTM);
    },
    previewSteps() {
      return [
        {
          headline: this.$t("views.serviceplanner.preview.planning.headline"),
          description: this.$t(
            "views.serviceplanner.preview.planning.description",
          ),
          bullets: [
            this.$t("views.serviceplanner.preview.planning.bullet-1"),
            this.$t("views.serviceplanner.preview.planning.bullet-2"),
            this.$t("views.serviceplanner.preview.planning.bullet-3"),
          ],
          icon: "calculator",
          image: this.localizedImage("planning-parts"),
          overlay: this.localizedImage("planning-parts-overlay"),
        },
        {
          headline: this.$t("views.serviceplanner.preview.offer.headline"),
          description: this.$t(
            "views.serviceplanner.preview.offer.description",
          ),
          bullets: [
            this.$t("views.serviceplanner.preview.offer.bullet-1"),
            this.$t("views.serviceplanner.preview.offer.bullet-2"),
          ],
          icon: "file-contract",
          image: this.localizedImage("offers"),
          overlay: this.localizedImage("offers-overlay"),
        },
        {
          headline: this.$t(
            "views.serviceplanner.preview.documenting.headline",
          ),
          description: this.$t(
            "views.serviceplanner.preview.documenting.description",
          ),
          bullets: [
            this.$t("views.serviceplanner.preview.documenting.bullet-1"),
            this.$t("views.serviceplanner.preview.documenting.bullet-2"),
            this.$t("views.serviceplanner.preview.documenting.bullet-3"),
          ],
          icon: "clipboard-check",
          image: this.localizedImage("documenting"),
        },
      ];
    },
  },
  methods: {
    localizedImage(name) {
      const LANGUAGE = this.$root.$i18n.locale === "de" ? "de" : "en";
      return require(`../../assets/serviceplanner-${name}-${LANGUAGE}.webp`);
    },
  },
  data: () => ({
    bannerStyle: `background: linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(${bannerImage}) center / cover no-repeat`,
  }),
};
</script>

<style lang="scss">
// NOTE: shifts image and overlay as a block, so the overhang does not push the
// NOTE: pair off centre within the column. widths stay untouched
#serviceplanner-landingpage .image.has-overlay {
  margin-top: -0.5rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  margin-left: -0.5rem;
}

#serviceplanner-landingpage .preview-overlay {
  position: absolute;
  right: -1rem;
  bottom: -1rem;
  width: 66%;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

#serviceplanner-landingpage .banner-mockup {
  @media screen and (min-width: 1024px) {
    display: block;
    width: calc(100% + 3rem);
    max-width: none;
    margin-right: -3rem;
  }
}
</style>
